import type { FilterOption } from '@/components/common-table/FilterSearchBar'
import type { ReactNode } from 'react'
import { createContext, useContext } from 'react'

export interface CustomViewsConfig {
	pageKey: string
	columns: Array<{ id: string; label: string; formatValue?: (value: unknown) => string }>
	data: Record<string, unknown>[]
	customViewFilters: FilterOption[] // Separate state for custom views
	onCustomViewFiltersChange: (filters: FilterOption[]) => void // Separate callback
	clearActiveView?: () => void
	overflowTabs?: Array<{
		name: string
		count: number
		key: string
		onClick?: () => void
		isActive: boolean
		showCount?: boolean
	}>
	onTabReorder?: (tabKeys: string[]) => void
}

export interface CustomViewsContextValue {
	config: CustomViewsConfig | null
	setConfig: (config: CustomViewsConfig | null) => void
	isViewCreatorOpen: boolean
	setIsViewCreatorOpen: (isOpen: boolean) => void
	currentFilters: FilterOption[]
	setCurrentFilters: (filters: FilterOption[]) => void
	onOpenChange?: (isOpen: boolean) => void
	editingViewId: string | null
	setEditingViewId: (id: string | null) => void
	rightContent: ReactNode
	setRightContent: (content: ReactNode) => void
	registerClearFilters: (fn: (() => void) | null) => void
	clearRegularFilters: () => void
	activeViewId: string | null
	setActiveViewId: (id: string | null) => void
}

// Component-free module keeps this context's identity stable across Fast Refresh;
// co-locating it with a component export makes HMR mint a new context and desync the provider.
export const CustomViewsContext = createContext<CustomViewsContextValue | undefined>(undefined)

export function useCustomViewsContext() {
	const context = useContext(CustomViewsContext)
	if (!context) {
		throw new Error('useCustomViewsContext must be used within CustomViewsProvider')
	}
	return context
}
